import React, { useState, useEffect } from 'react';
import { getPushUpsStats } from '../../../utils/FamilyApiService';
import styled from 'styled-components'
import SixSeven from './SixSeven';

const MILESTONE_GIFS = [
    { currentDayStreak: 67, gifSource: '6-7-cat.gif' },
    { currentDayStreak: 100, gifSource: 'mcdonald.gif' },
    { currentDayStreak: 200, gifSource: 'shopping.gif' },
    { currentDayStreak: 365, gifSource: 'cake.gif' },
];

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 20px;
    margin-top: 30px;
`;

const ProfileSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 10px;
`;

const ImageStyled = styled.img`
    height: 180px;
    border-radius: 30px;
    object-fit: cover;
    border: 6px solid rgba(255, 255, 255, 0.4);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.8);
`;

const StatusEmoji = styled.div`
    font-size: 48px;
    margin-top: -24px;
    background: white;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
`;

const StreakSection = styled.div`
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    padding: 24px;
    width: 100%;
`;

const StreakNumber = styled.div`
    font-size: 100px;
    font-weight: 800;
    text-align: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
    margin-bottom: 8px;
`;

const DaysGrid = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    padding: 20px;
    width: fit-content;
    max-width: 100%;
    justify-content: center;
    align-items: center;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.7);
    border: 3px solid rgba(255, 255, 255, 0.8);
    position: relative;
    flex-wrap: nowrap;
    
    &::before {
        content: '';
        position: absolute;
        top: -3px;
        left: -3px;
        right: -3px;
        bottom: -3px;
        background: linear-gradient(135deg, #667eea, #764ba2, #667eea);
        border-radius: 20px;
        z-index: -1;
    }
`;

const DayCard = styled.div`
    background: ${props => {
        if (props.status === 0) return '#73e269';
        if (props.status === 1) return '#f19393';
        if (props.status === 2) return '#87CEEB';
        if (props.status === 3) return '#e0e5e9';
        return '#f19393';
    }};
    width: 70px;
    height: 70px;
    min-width: 70px;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    position: relative;
    flex-shrink: 0;
`;

const DayName = styled.div`
    position: absolute;
    top: 4px;
    font-size: 13px;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const DayIcon = styled.div`
    position: absolute;
    top: 20px;
    font-size: 32px;
    font-weight: ${props => props.showBalance ? '800' : '400'};
    color: ${props => props.showBalance ? '#b60909' : '#030303ff'};
`;

const FreezeIcon = styled.div`
    position: absolute;
    bottom: -10px;
    right: -8px;
    font-size: 25px;
`;

const LoadingContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px;
`;

const ErrorContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px;
    cursor: pointer;
`;

const ErrorEmoji = styled.div`
    font-size: 64px;
`;

const PushUpsStreak = ({ person, imageSource }) => {

    const [loading, setLoading] = useState(false);
    const [pushUpsStreakData, setPushUpsStreakData] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [activeMilestoneGif, setActiveMilestoneGif] = useState(null);

    async function getPushUpsStatsData() {
        setLoading(true);

        try {
            var pushUpsStats = await getPushUpsStats(person);
            setPushUpsStreakData(pushUpsStats);
            setHasError(false);

            const milestone = MILESTONE_GIFS.find(
                item => item.currentDayStreak === pushUpsStats.currentDayStrek
            );
            setActiveMilestoneGif(milestone ? milestone.gifSource : null);
        }
        catch (error) {
            console.error('Feil ved henting av push-ups data:', error);
            setHasError(true);
        }

        setLoading(false);
    }

    useEffect(() => {
        if (!activeMilestoneGif) return;

        const timer = setTimeout(() => {
            setActiveMilestoneGif(null);
        }, 5000);

        return () => clearTimeout(timer);
    }, [activeMilestoneGif]);

    const getEmoji = () => {
        if (pushUpsStreakData?.hasValidStreakToday) {
            return '👍';
        }
        if (pushUpsStreakData?.isBehindStreak) {
            return '😱';
        }
        if (!pushUpsStreakData?.hasValidStreakToday && !pushUpsStreakData?.isBehindStreak) {
            return '⏳';
        }

        return '';
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 0:
                return '✓';
            case 1:
                return '☢️';
            case 2:
                return '✓';
            case 3:
                return '?';
            default:
                return '';
        }
    };

    useEffect(() => {
        getPushUpsStatsData();
    }, []);

    if (loading) {
        return (
            <LoadingContainer>
                <div className="weatherSpinner"></div>
            </LoadingContainer>
        );
    }
    else if (hasError) {
        return (
            <ErrorContainer onClick={() => getPushUpsStatsData()}>
                <ErrorEmoji>❌</ErrorEmoji>
            </ErrorContainer>
        );
    }
    else if (activeMilestoneGif) {
        return (
            <LoadingContainer>
                <SixSeven gifSource={activeMilestoneGif} />
            </LoadingContainer>
        );
    }
    else {
        return (
            <Container onClick={() => getPushUpsStatsData()}>
                <ProfileSection>
                    <ImageStyled src={imageSource} />
                    <StatusEmoji>{getEmoji()}</StatusEmoji>
                </ProfileSection>

                <StreakSection>
                    <StreakNumber>{pushUpsStreakData?.currentDayStrek}</StreakNumber>
                </StreakSection>

                <DaysGrid>
                    {pushUpsStreakData?.streakDays?.slice().reverse().map((day, index) => (
                        <DayCard key={index} status={day.streakStatus}>
                            <DayName>{day.dayName}</DayName>
                            <DayIcon
                                showBalance={pushUpsStreakData?.isBehindStreak && index === pushUpsStreakData?.streakDays?.length - 1}
                            >
                                {pushUpsStreakData?.isBehindStreak && index === pushUpsStreakData?.streakDays?.length - 1
                                    ? pushUpsStreakData?.balance
                                    : getStatusIcon(day.streakStatus)}
                            </DayIcon>
                            {day.streakStatus === 2 && <FreezeIcon>❄️</FreezeIcon>}
                        </DayCard>
                    ))}
                </DaysGrid>
            </Container>
        );
    }
};

export default PushUpsStreak;